<template>
	<van-cascader
		v-model="vantValue"
		:title="props.title"
		:show-header="!!props.title"
		:active-color="props.color ?? $config.public.colors.primary"
		:field-names="{text: props.textProp, value: vantValueProp, children: props.childrenProp}"
		:options="vantOptions"
		:closeable="false"
		@change="onChange">

		<template v-if="filterable" #options-top>
			<div class="px-16 pt-16 pb-4">
				<form-input v-model="filterValue" type="search" :placeholder="$t('placeholder.search')" inverted hide-details clearable/>
			</div>
		</template>

		<template #option="{option, selected}">
			<skeleton v-if="option.loading" :height="20" width="100%" rounded/>
			<div v-else-if="isTargetedByFilter(option)" class="flex items-center flex-grow-x-1 overflow-hidden">
				<lazy-avatar v-if="props.imageProp && option[props.imageProp]" :src="option[props.imageProp]"
							 :size="16"
							 class="mr-16"
							 :ratio="option[props.imageProp].ratio ?? 1"
							 contain
							 :tile="props.imageTile"/>
				<div class="flex items-center justify-between flex-grow-x-1 overflow-hidden">
					<span class="truncate" :title="option[props.textProp]">{{ option[props.textProp] }}</span>
					<span v-if="props.helpProp && !selected" class="flex-grow-x-1 truncate text-gray-400 text-2xs ml-16 text-right" :title="option[props.helpProp]">{{ option[props.helpProp] }}</span>
				</div>
			</div>
		</template>

	</van-cascader>
</template>

<script setup lang="ts">
import {ref, watch, computed, nextTick, type Ref} from "vue";
import type { CascaderOption } from 'vant';
import 'vant/es/cascader/style';

const props = withDefaults(defineProps<{
	// The selected value to keep in sync.
	// If specified as string or number, the value will be set to the last selected option's value.
	// If specified as an array of strings or numbers, the array contains the values for each selected step's option.
	modelValue?: string|number|(string|number)[],

	options: CascaderOption[],
	title?: string,
	color?: string,
	textProp?: string,
	valueProp?: string,
	helpProp?: string,
	imageProp?: string,
	imageTile?: boolean,
	childrenProp?: string,

	// The prop of the options that holds the amount of children of the option.
	// Also defines the existence of children.
	childrenCountProp?: string,

	childrenLoader?: (option: CascaderOption) => Promise<void>,

	// The text of the option to unselect the selection of the current step with the value 'null'
	unselect?: string,

	// Whether to show the search input to filter the options
	filterable?: boolean,
}>(), {
	textProp: 'text',
	valueProp: 'value',
	childrenProp: 'children'
});

const emit = defineEmits(['update:modelValue', 'selected']);

const wantsArrayValue = Array.isArray(props.modelValue);

const filterValue: Ref<String> = ref(null);

// The values of the options provided to the Vant component must be unique, even if nested, whereby we use a special vant value with a prefix of the higher level value.
const vantValueProp = '_cascader_value';
function buildVantOption(option: CascaderOption, parentOption: undefined|CascaderOption = undefined) {
	const vantOption = {
		...option,
		[vantValueProp]: (parentOption ? (option[props.valueProp] + '-') : '') + option[props.valueProp]
	};

	if (option[props.childrenProp]?.length) {
		vantOption[props.childrenProp] = buildVantOptions(option[props.childrenProp], option);
	}

	return vantOption;
}
function buildVantOptions(options: CascaderOption[], parentOption: undefined|CascaderOption = undefined) {
	const vantOptions = options.map((option) => buildVantOption(option, parentOption));

	if (props.unselect) {
		vantOptions.unshift({
			[props.textProp]: props.unselect,
			[vantValueProp]: null
		});
	}

	return vantOptions;
}
const vantOptions = computed(() => {
	return buildVantOptions(props.options);
});

function isTargetedByFilter(option: CascaderOption) {
	if (!filterValue.value?.length) {
		return true;
	}
	const normalizedFilterValue = normalizeValueForFilter(filterValue.value);

	return normalizeValueForFilter(option[props.textProp]).includes(normalizedFilterValue) || normalizeValueForFilter(option[props.valueProp]).includes(normalizedFilterValue);
}

function normalizeValueForFilter(value: string|number) {
	return value ? value.toString().toLowerCase() : '';
}

function resolveVantValueFromSubmitValue() {
	if (!wantsArrayValue) {
		return submitValue.value;
	}

	let vantValue = null;
	let options = vantOptions.value;
	const submitValueArray = Array.isArray(submitValue.value) ? submitValue.value : [submitValue.value];
	submitValueArray.forEach((itValue) => {
		const optionMatch = itValue ? options.find((option) => option[props.valueProp] === itValue) : null;
		if (optionMatch) {
			options = optionMatch[props.childrenProp] ?? [];
			vantValue = optionMatch[vantValueProp];
		}
	});

	return vantValue;
}

const submitValue = ref(props.modelValue);
watch(() => props.modelValue, (newValue) => {
	submitValue.value = newValue;
	vantValue.value = resolveVantValueFromSubmitValue();
});
watch(submitValue, (newValue) => {
	emit('update:modelValue', newValue);
});
const vantValue = ref(resolveVantValueFromSubmitValue());

async function onChange({ value, selectedOptions }) {
	let valuePropOption = null;
	filterValue.value = null;

	if (wantsArrayValue) {
		submitValue.value = selectedOptions.map((option) => option[props.valueProp]);

		let propOptions = props.options;
		submitValue.value.forEach((itValue) => {
			valuePropOption = propOptions.find((option) => option[props.valueProp] === itValue);
			propOptions = valuePropOption?.[props.childrenProp] ?? [];
		});
	} else {
		submitValue.value = value;
		valuePropOption = selectedOptions.find((option) => option[vantValueProp] === submitValue.value);
	}

	if (!valuePropOption || (!valuePropOption[props.childrenProp]?.length && (!props.childrenCountProp || !valuePropOption[props.childrenCountProp]))) {
		// We must wait until the submit value is updated on the parent for correct ChipModal applies, e.g., on events index page.
		nextTick(() => {
			emit('selected', submitValue.value);
		})
		return;
	}

	if (valuePropOption && props.childrenLoader) {
		// Set loading state
		valuePropOption[props.childrenProp] = Array(valuePropOption[props.childrenCountProp]).fill({loading: true});
		try {
			await props.childrenLoader(valuePropOption);
		} catch {
			valuePropOption[props.childrenProp] = [];
		}
	}
}

</script>

<style>
/*********************
  Inputs
**********************/
.app .van-popup--left .van-cascader__options, .app .van-popup--right .van-cascader__options {
  height: 100%;
}
.app .van-cascader__options .van-cascader__option {
  font-size: 15px;
  padding: 8px 0;
}
.app .van-cascader__options .van-cascader__option:hover {
  background-color: #FBFBFB;
}
.app .van-cascader__options .van-cascader__option:empty, .app .van-cascader__options .van-cascader__option:has(.van-cascader__selected-icon:only-child) {
  padding: 0;
}
.app .van-cascader__options .van-cascader__option .van-cascader__selected-icon:only-child {
  display: none;
}
</style>